import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Grid,
    TextField, MenuItem, Switch, Button, Alert, CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../../constants";
import { loyaltyFetch } from "../../../hooks/loyaltyFetch";

const EditRewardCategoryDialog = ({ open, rewardCategory, onClose, onSaved, couponTypes = [] }) => {
    const [form, setForm] = useState({ name: "", description: "", coupon_type_id: "", active: true });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (rewardCategory) {
            setForm({
                name: rewardCategory.name || "",
                description: rewardCategory.description || "",
                coupon_type_id: rewardCategory.coupon_type_id || "",
                active: rewardCategory.active ?? true,
            });
            setError(null);
        }
    }, [rewardCategory]);

    const handleChange = (field) => (e) =>
        setForm((p) => ({ ...p, [field]: e.target.value }));

    const isValid = form.name.trim() && form.coupon_type_id;

    const handleSubmit = async () => {
        if (!isValid || !rewardCategory) return;
        setSubmitting(true);
        setError(null);
        try {
            const body = {
                name: form.name.trim(),
                description: form.description.trim() || null,
                coupon_type_id: form.coupon_type_id,
                active: form.active,
            };
            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/reward-categories/${rewardCategory.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json", "X-Brand": rewardCategory.brand },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(e?.detail || `Erreur HTTP ${res.status}`);
            }
            onSaved();
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (!rewardCategory) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.rewards.gradient }}>
                        <EditIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                            Modifier la Catégorie
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">{rewardCategory.name}</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth required label="Nom de la catégorie" value={form.name}
                            onChange={handleChange("name")} size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Description" value={form.description}
                            onChange={handleChange("description")} size="small" multiline rows={2}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth required select label="Type de Coupon associé"
                            value={form.coupon_type_id} onChange={handleChange("coupon_type_id")}
                            size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
                            {couponTypes.map((ct) => (
                                <MenuItem key={ct.id} value={ct.id}>{ct.name}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Typography fontSize={13} fontWeight={600} fontFamily="Montserrat, sans-serif">Actif</Typography>
                            <Switch checked={form.active}
                                onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.rewards.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.rewards.main },
                                }} />
                        </Box>
                    </Grid>
                </Grid>
                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    sx={{ background: SECTION_COLORS.rewards.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    {submitting ? "Enregistrement..." : "Enregistrer"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditRewardCategoryDialog;
